import { Subject } from 'rxjs';
import { GameServices } from '../../services/game-services';
import { CardViewModel } from '../cards/card-view-model';
import { TwentyOne } from 'game-logic';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';

export class VerticalCardStackViewModel {
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get cardViewModelChanges$() {
    return this.cardViewModelChanges.asObservable();
  }

  private cardViewModels: Map<number, CardViewModel> = new Map();
  private readonly cardViewModelChanges = new Subject<{
    type: 'add' | 'removeToWaste' | 'removeToBottom';
    viewModel: CardViewModel;
    duration: number;
    delay: number;
    instant: boolean;
  }>();

  constructor(
    private gameServices: GameServices,
    readonly pileId: TwentyOne.PileId,
  ) {}

  public resetGameScene() {
    this.clearAllCards();
  }

  public async addCard(
    cardColumn: TwentyOne.CardColumn,
    cardIndex: number | undefined,
    instant: boolean,
  ): Promise<void> {
    for (const [itemIndex, card] of cardColumn.allFaceCards.entries()) {
      if (card.index === cardIndex) {
        await this.stackCard(card, itemIndex, instant);
      }
    }
  }

  public async removeAllCards(instant: boolean): Promise<void> {
    if (this.cardViewModels.size === 0) {
      return;
    }

    let delayMs = UxMetrics.CardStackDropDelay * 2;
    for (const cardViewModel of Array.from(this.cardViewModels.values()).slice().reverse()) {
      this.cardViewModelChanges.next({
        type: 'removeToBottom',
        viewModel: cardViewModel,
        duration: UxMetrics.CardClearDelay,
        delay: delayMs,
        instant,
      });
      delayMs += UxMetrics.CardStackDropDelay;
    }
    this.cardViewModels.clear();
  }

  private async stackCard(card: TwentyOne.Card, index: number, instant: boolean): Promise<void> {
    const cardViewModel = new CardViewModel(this.gameServices, card, index);
    cardViewModel.refresh();

    const durationMs = UxMetrics.CardStackDuration;
    this.cardViewModels.set(cardViewModel.cardIndex, cardViewModel);
    this.cardViewModelChanges.next({
      type: 'add',
      viewModel: cardViewModel,
      duration: durationMs,
      delay: 0,
      instant,
    });
    if (!instant) {
      await delay(durationMs);
    }
  }

  private async clearAllCards(): Promise<void> {
    if (this.cardViewModels.size === 0) {
      return;
    }

    const durationMs = UxMetrics.CardClearDelay / this.cardViewModels.size;
    for (const cardViewModel of Array.from(this.cardViewModels.values()).slice().reverse()) {
      this.cardViewModelChanges.next({
        type: 'removeToWaste',
        viewModel: cardViewModel,
        duration: UxMetrics.CardClearDelay,
        delay: 0,
        instant: false,
      });
      await delay(durationMs);
    }
    this.cardViewModels.clear();
  }
}
